// src/app/core/firebase/firebase-auth.service.ts
import { Injectable, signal } from '@angular/core';
import {
    signInWithEmailAndPassword,
    signOut,
    onAuthStateChanged,
    User,
} from 'firebase/auth';
import { firebaseAuth } from './firebase.client';

@Injectable({ providedIn: 'root' })
export class FirebaseAuthService {
    // ✅ estado interno (legacy)
    private _user: User | null = null;

    // ✅ estado reactivo para UI/guards
    readonly userSig = signal<User | null>(null);
    readonly readySig = signal<boolean>(false);

    // ✅ para guards / bootstrap
    private _readyResolved = false;
    private _readyPromise: Promise<User | null>;

    // ✅ para evitar fugas (HMR/tests)
    private _unsubscribeAuth?: () => void;

    constructor() {
        this._readyPromise = new Promise<User | null>((resolve) => {
            this._unsubscribeAuth = onAuthStateChanged(firebaseAuth, (u) => {
                this._user = u;

                // ✅ sincroniza signal (reactivo)
                this.userSig.set(u);

                // ✅ ready solo 1 vez
                if (!this._readyResolved) {
                    this._readyResolved = true;
                    this.readySig.set(true);
                    resolve(u);
                }
            });
        });
    }

    // ✅ getters legacy (siguen funcionando)
    get user(): User | null {
        return this._user;
    }

    get isLoggedIn(): boolean {
        return !!this._user;
    }

    /** ✅ Espera a que Firebase Auth termine de resolver el estado */
    whenReady(): Promise<User | null> {
        return this._readyPromise;
    }

    /** ✅ Login manual (pantalla) */
    async login(email: string, password: string): Promise<User> {
        const cred = await signInWithEmailAndPassword(firebaseAuth, email, password);

        this._user = cred.user;
        this.userSig.set(cred.user);

        // ✅ si por alguna razón el login ocurre antes de que onAuthStateChanged dispare
        if (!this._readyResolved) {
            this._readyResolved = true;
            this.readySig.set(true);
        }

        return cred.user;
    }


    /** ✅ Logout */
    async logout(): Promise<void> {
        await signOut(firebaseAuth);

        this._user = null;
        this.userSig.set(null);

        // ✅ por consistencia: si alguien llama logout antes de resolver onAuthStateChanged
        if (!this._readyResolved) {
            this._readyResolved = true;
            this.readySig.set(true);
        }
    }

    /**
     * ✅ Limpieza (útil en HMR/tests).
     * Nota: en apps normales el servicio vive toda la app y no se destruye,
     * pero esto evita fugas en recargas/hot reload.
     */
    dispose(): void {
        try {
            this._unsubscribeAuth?.();
        } finally {
            this._unsubscribeAuth = undefined;
        }
    }
}
